/*
 * Intelligent Fabric — Backstage theme (Midnight Indigo)
 *
 * Translates the approved Admin Console design tokens
 * (.planning/sketches/themes/default.css) into Backstage's real theming API.
 * Backstage's frontend is React/MUI, so the polished dark operator-console look
 * ships as a `createUnifiedTheme` — NOT a different framework.
 *
 * Registered in packages/app/src/App.tsx via createApp({ themes: [...] }).
 * Requires the source-build migration (see k8s/backstage/app/MIGRATION.md) —
 * the stock upstream image only ships compiled dist/ and cannot be themed.
 */
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

// --- Design tokens (mirror of themes/default.css) ---
const ink = {
  bg: '#0a0d18',
  bgElev: '#0e1222',
  surface: '#141a2e',
  surface2: '#1a2138',
  border: '#242c45',
  borderSoft: '#1c2237',
  text: '#e8ecf7',
  textMuted: '#8b95b3',
  textFaint: '#59617e',
  primary: '#21e0c4', // electric teal
  primaryHover: '#4fe9d2',
  primaryInk: '#06231f',
  violet: '#8b7bf7',
  success: '#34d399',
  warn: '#fbbf24',
  danger: '#f87171',
  info: '#60a5fa',
};

const fabricGradient = `linear-gradient(135deg, ${ink.primary} 0%, #5b8df7 55%, ${ink.violet} 100%)`;

export const intelligentFabricTheme = createUnifiedTheme({
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif",
  defaultPageTheme: 'home',
  palette: {
    ...palettes.dark,
    mode: 'dark',
    background: {
      default: ink.bg,
      paper: ink.surface,
    },
    primary: { main: ink.primary, dark: ink.primaryHover },
    secondary: { main: ink.violet },
    text: {
      primary: ink.text,
      secondary: ink.textMuted,
    },
    divider: ink.borderSoft,
    error: { main: ink.danger },
    warning: { main: ink.warn },
    success: { main: ink.success },
    info: { main: ink.info },
    status: {
      ok: ink.success,
      warning: ink.warn,
      error: ink.danger,
      running: ink.info,
      pending: ink.textFaint,
      aborted: ink.textFaint,
    },
    background2: ink.bgElev as unknown as string,
    // Left sidebar — the operator nav from the mockup
    navigation: {
      background: ink.bgElev,
      indicator: ink.primary,
      color: ink.textMuted,
      selectedColor: ink.text,
      navItem: { hoverBackground: ink.surface },
      submenu: { background: ink.bg },
    },
    banner: {
      info: ink.info,
      error: ink.danger,
      text: ink.text,
      link: ink.primary,
      warning: ink.warn,
    },
    errorBackground: '#2a1620',
    warningBackground: '#2a2410',
    infoBackground: '#0e1c2e',
    border: ink.border,
  } as any,
  // Page header banners use the woven-fabric gradient
  pageTheme: {
    home: genPageTheme({ colors: [ink.primary, ink.violet], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: [ink.primary, '#5b8df7'],
      shape: shapes.wave2,
    }),
    service: genPageTheme({ colors: [ink.primary, ink.violet], shape: shapes.wave }),
    tool: genPageTheme({ colors: [ink.violet, '#5b8df7'], shape: shapes.round }),
    apis: genPageTheme({ colors: ['#5b8df7', ink.primary], shape: shapes.wave2 }),
    other: genPageTheme({ colors: [ink.primary, ink.violet], shape: shapes.wave }),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: `radial-gradient(900px 500px at 82% -8%, ${ink.primary}14, transparent 60%), radial-gradient(700px 500px at -5% 110%, ${ink.violet}14, transparent 55%)`,
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${ink.borderSoft}`,
          borderRadius: 14,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: `linear-gradient(180deg, ${ink.surface}, ${ink.bgElev})`,
          border: `1px solid ${ink.borderSoft}`,
          borderRadius: 14,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 10, fontWeight: 600 },
        containedPrimary: {
          background: fabricGradient,
          color: ink.primaryInk,
          '&:hover': { filter: 'brightness(1.06)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 9999, fontWeight: 600, fontSize: '0.72rem' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          fontSize: '0.69rem',
          color: ink.textFaint,
          borderBottom: `1px solid ${ink.borderSoft}`,
        },
        root: { borderBottom: `1px solid ${ink.borderSoft}` },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: ink.primary, height: 3 } },
    },
  },
});

export default intelligentFabricTheme;
